import { useEffect, useId, useRef, useState } from "react";
import type { Analect } from "../engine";
import { highlightedSegments, type MatchRange } from "./conceptIndex";
import { currentAnnotation, isAnnotationMarker, selectAnnotation, type AnnotationSelection } from "./annotationSelection";

/** Source text is unchanged. Only inline annotation marks become small, accessible controls. */
export function AnnotatedText({ passage, ranges = [], heading = "h1", completeNotes = false }: { passage: Analect; ranges?: MatchRange[]; heading?: "h1" | "p"; completeNotes?: boolean }) {
  const TextElement = heading;
  let offset = 0;
  let occurrence = 0;
  const [selection, setSelection] = useState<AnnotationSelection | null>(null);
  const [showAll, setShowAll] = useState(false);
  const note = currentAnnotation(passage, selection);
  const noteId = useId();
  const allTrigger = useRef<HTMLButtonElement>(null);
  const triggers = useRef(new Map<number, HTMLButtonElement>());
  useEffect(() => { setSelection(null); setShowAll(false); }, [passage.id, passage.text, passage.annotations]);
  const closeNote = () => {
    if (note) triggers.current.get(note.offset)?.focus();
    else if (showAll) allTrigger.current?.focus();
    setSelection(null);
    setShowAll(false);
  };
  return <>
    <TextElement className="annotated-passage">{passage.text.split(/([⑴-⒇])/).map((part, index) => {
      const start = offset; offset += part.length;
      if (isAnnotationMarker(part)) {
        occurrence += 1;
        const number = part.charCodeAt(0) - 0x2473;
        const expanded = note?.offset === start;
        return <sup key={index}><button type="button"
          ref={element => { if (element) triggers.current.set(start, element); else triggers.current.delete(start); }}
          aria-label={`註釋 ${number}，原文第 ${occurrence} 處`}
          aria-expanded={expanded} aria-controls={expanded ? noteId : undefined}
          onKeyDown={event => { if (expanded && event.key === "Escape") { event.stopPropagation(); closeNote(); } }}
          onClick={() => { setShowAll(false); setSelection(expanded ? null : selectAnnotation(passage, start)); }}>{number}</button></sup>;
      }
      return <span key={index}>{highlightedSegments(part, ranges, start).map((segment, i) => segment.match ? <mark key={i}>{segment.text}</mark> : segment.text)}</span>;
    })}</TextElement>
    {completeNotes && <button className="all-notes-toggle" ref={allTrigger} aria-controls={showAll ? noteId : undefined} aria-expanded={showAll} onClick={() => { setSelection(null); setShowAll(!showAll); }}>全部註釋</button>}
    {(note || showAll) && <aside id={noteId} className="inline-annotation" aria-live="polite"
      onKeyDown={event => { if (event.key === "Escape") { event.stopPropagation(); closeNote(); } }}>
      <strong>{showAll ? '楊伯峻注釋' : `註 ${note!.number}`}</strong>
      {note && !note.body && <p className="annotation-unresolved">此標記的原注對應不明，保留完整原注供核對。</p>}
      {note?.body && note.sourceNumber !== note.number && <small>{note.sourceNumber === null ? '原注未標號' : `原注標號 ${note.sourceNumber}`} · 依原文字詞對應</small>}
      <p>{(showAll ? passage.annotations : note?.body ?? note?.annotations) || "此章來源未附註釋。"}</p>
      <button type="button" onClick={closeNote}>收起註釋</button>
    </aside>}
  </>;
}
